// ============================================
// FILE: components/ProductTable.jsx
//
// Changes vs old version:
//  - No more polling or manual refetch on mount
//  - Socket.io join:lead room for per-lead real-time updates
//  - fetchProducts (refetch) only called on bulk import success
//  - Removed fetchLeadById (unused in this component)
//  - API_URL pulled from env via useLeads hook
// ============================================

import React, { useState, useMemo, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { MantineReactTable } from 'mantine-react-table';
import {
  MantineProvider,
  Box,
  Modal,
  TextInput,
  Select,
  Button,
  ActionIcon,
  Group,
  SimpleGrid,
  Notification,
  Text,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import { useLeads } from '../hooks/useLeads';
import {
  configurationList,
  propertyTypeList,
  possessionTypeList,
  cityList,
  requirementTypeList,
  serviceTypeList,
  salesRepList,
  preSalesRepList,
  sourceList,
  channelList,
  mediumList,
} from '../constants/formOptions';
import BulkImportModal from './BulkImportModal';
import { useNavigate } from 'react-router-dom';

export default function ProductTable() {
  const userId   = window.localStorage.getItem('projectUserUID');
  const userRole = window.localStorage.getItem('projectUserRole');
  const userName = window.localStorage.getItem('projectUserDisplayName');
  const navigate = useNavigate();

  // ─── Server-side state ─────────────────────────────────────────
  const [pagination,    setPagination]    = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting,       setSorting]       = useState([]);

  const {
    productsApiData,
    totalCount,
    isLoading,
    isFetching,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    isCreating,
    isUpdating,
    isDeleting,
  } = useLeads({ userId, userRole, pagination, columnFilters, sorting });

  // ─── Modal / form state ────────────────────────────────────────
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen,   setEditModalOpen]   = useState(false);
  const [formValues,      setFormValues]      = useState({});
  const [editRowId,       setEditRowId]       = useState(null);
  const [projectId,       setProjectId]       = useState('');
  const [notification,    setNotification]    = useState(null);
  const [bulkImportOpen,  setBulkImportOpen]  = useState(false);

  // ─── File state ────────────────────────────────────────────────
  const [files,         setFiles]         = useState({ floorPlan: [] });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileError,     setFileError]     = useState(null);

  const resetFiles = () => {
    setFiles({ floorPlan: [] });
    setFileError(null);
  };

  // ─── Generate project ID when create modal opens ───────────────
  useEffect(() => {
    if (createModalOpen && !projectId) generateProjectId();
  }, [createModalOpen]);

  const generateProjectId = async () => {
    try {
      const API_URL = process.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`https://api-4h4srbodqa-uc.a.run.app/api/generate-project-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to generate project ID');
      const result = await res.json();
      setProjectId(result.projectId);
      setFormValues((prev) => ({ ...prev,
         projectId: result.projectId,
           ...(userRole === 'preSales'
    ? { presales_name: userName, presales_id: userId }
    : {}),
  // salesPerson: auto-assign themselves, hide dropdown
  ...(userRole === 'salesperson'
    ? { sales_rep: userName, sales_rep_id: userId }
    : {}),
   }));
    } catch (err) {
      showNotification('error', 'Failed to generate project ID. Please try again.');
    }
  };

  // ─── Notifications ─────────────────────────────────────────────
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // ─── Form helpers ──────────────────────────────────────────────
  const handleChange = (field, value) =>
    setFormValues((prev) => ({ ...prev, [field]: value }));

  const toDisplayDate = (val) => {
    if (!val) return '';
    const [y, m, d] = val.split('-');
    return d && m && y ? `${d}-${m}-${y}` : val;
  };

  const toInputDate = (val) => {
    if (!val) return '';
    const [d, m, y] = val.split('-');
    return d && m && y ? `${y}-${m}-${d}` : val;
  };

  // ─── File input ────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const { name } = e.target;
    const selected  = Array.from(e.target.files);
    const allowed   = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];

    for (const file of selected) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError(`${file.name} is too large. Maximum 5 MB.`);
        return;
      }
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (!allowed.includes(ext)) {
        setFileError(`Invalid type for ${file.name}. Allowed: ${allowed.join(', ')}`);
        return;
      }
    }
    setFileError(null);
    setFiles((prev) => ({ ...prev, [name]: selected }));
  };

  // ─── Firebase upload helpers ───────────────────────────────────
  const uploadToFirebase = async (file, folder, index = null) => {
    if (!file) return null;
    const storage = getStorage();
    const ext     = file.name.slice(file.name.lastIndexOf('.'));
    const pid     = projectId || formValues.projectId;
    const path    = index !== null
      ? `${pid}/${folder}/${folder}_${index}${ext}`
      : `${pid}/${folder}/${folder}${ext}`;
    const snap = await uploadBytes(ref(storage, path), file);
    return getDownloadURL(snap.ref);
  };

  const uploadMultiple = async (fileArray, folder) => {
    if (!fileArray?.length) return [];
    return Promise.all(fileArray.map((f, i) => uploadToFirebase(f, folder, i)));
  };

  // ─── Create ────────────────────────────────────────────────────
  const handleCreateProduct = async () => {
    if (!formValues.full_name || !formValues.mobile_number) {
      showNotification('error', 'Full name and mobile number are required');
      return;
    }
    setUploadLoading(true);
    try {
      const floorPlanUrls = await uploadMultiple(files.floorPlan, 'site-images');
      createProduct(
        { ...formValues, floorPlan: floorPlanUrls },
        {
          onSuccess: () => {
            setCreateModalOpen(false);
            setFormValues({});
            resetFiles();
            setProjectId('');
            showNotification('success', 'Lead created successfully');
          },
          onError: (err) =>
            showNotification('error', err.response?.data?.message || 'Failed to create lead'),
        }
      );
    } catch (err) {
      showNotification('error', 'File upload failed: ' + err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Update ────────────────────────────────────────────────────
  const handleUpdateProduct = async () => {
    setUploadLoading(true);
    try {
      const floorPlanUrls = files.floorPlan.length
        ? await uploadMultiple(files.floorPlan, 'site-images')
        : formValues.floorPlan || [];

      updateProduct(
        { id: editRowId, leadData: { ...formValues, floorPlan: floorPlanUrls } },
        {
          onSuccess: () => {
            setEditModalOpen(false);
            setFormValues({});
            resetFiles();
            showNotification('success', 'Lead updated successfully');
          },
          onError: (err) =>
            showNotification('error', err.response?.data?.message || 'Failed to update lead'),
        }
      );
    } catch (err) {
      showNotification('error', 'File upload failed: ' + err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────
  const handleDeleteProduct = (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    deleteProduct(id, {
      onSuccess: () => showNotification('success', 'Lead deleted successfully'),
      onError:   (err) => showNotification('error', err.response?.data?.message || 'Failed to delete lead'),
    });
  };

  // ─── Edit click ────────────────────────────────────────────────
  const handleEditClick = (row) => {
    setEditRowId(row.id);
    setFormValues({ ...row });
    resetFiles();
    setEditModalOpen(true);
  };

  // ─── Filter change — reset to page 0 ──────────────────────────
  const handleColumnFiltersChange = (updater) => {
    setColumnFilters(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // ─── Columns ───────────────────────────────────────────────────
  const columns = useMemo(() => [
    { accessorKey: 'full_name',           header: 'Full Name',     size: 150 },
    { accessorKey: 'mobile_number',       header: 'Mobile',        size: 120 },
    { accessorKey: 'propertyName',        header: 'Property Name', size: 150 },
    { accessorKey: 'configuration',       header: 'Config',        size: 100 },
    { accessorKey: 'propertyType',        header: 'Property Type', size: 150 },
    { accessorKey: 'meeting_date',        header: 'Meeting Date',  size: 130 },
    { accessorKey: 'sales_rep',           header: 'Sales Rep',     size: 120 },
    { accessorKey: 'possession',          header: 'Possession',    size: 120 },
    { accessorKey: 'move_in_date',        header: 'Move In Date',  size: 120 },
    { accessorKey: 'budget',              header: 'Budget',        size: 120 },
    { accessorKey: 'leadAcquisitionDate', header: 'Acquisition Date',     size: 120 },

    { accessorKey: 'email_address',       header: 'Email',         size: 200 },
    { accessorKey: 'address',             header: 'Address',       size: 120 },
    { accessorKey: 'city',                header: 'City',          size: 120 },
    { accessorKey: 'requirementType',     header: 'Requirement',   size: 130 },
    { accessorKey: 'serviceType',         header: 'Service',       size: 120 },
    { accessorKey: 'presales_name',       header: 'PreSales Rep',  size: 120 },
    { accessorKey: 'source',              header: 'Source',        size: 120 },
    { accessorKey: 'channel',             header: 'Channel',       size: 120 },
    { accessorKey: 'medium',             header: 'Medium',         size: 100 },
    { accessorKey: 'campaign',            header: 'Campaign',      size: 120 },
  ], []);

  // ─── File upload UI ────────────────────────────────────────────
  const FileUploadField = ({ label, name, accept, currentUrls = [] }) => (
    <Box>
      <Text size="sm" fw={500} mb={4}>{label}</Text>
      <label style={{
        display: 'block', border: '1px solid #ced4da', borderRadius: 4,
        padding: '8px 12px', cursor: 'pointer', fontSize: 14,
        color: files[name]?.length > 0 ? '#228be6' : '#868e96',
        backgroundColor: '#fff',
      }}>
        <input
          type="file" name={name} accept={accept} multiple
          onChange={handleFileChange} style={{ display: 'none' }}
        />
        {files[name]?.length > 0
          ? `${files[name].length} file(s) selected`
          : currentUrls.length > 0
            ? `${currentUrls.length} existing file(s) — click to replace`
            : 'Choose file(s)...'}
      </label>
      {!files[name]?.length && currentUrls.map((url, i) => (
        <Text key={i} size="xs" color="blue" component="a"
          href={url} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 4 }}>
          View file {i + 1}
        </Text>
      ))}
    </Box>
  );

  // ─── Shared form fields ────────────────────────────────────────
  const renderFormFields = (isEdit = false) => (
    <Box>
      {fileError && (
        <Box mb="md" p="sm"
          style={{ backgroundColor: '#fff5f5', borderRadius: 4, border: '1px solid #ffc9c9' }}>
          <Text color="red" size="sm">{fileError}</Text>
        </Box>
      )}
      <SimpleGrid cols={3} spacing="xl" breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
        <TextInput label="Full Name" required
          value={formValues.full_name || ''}
          onChange={(e) => handleChange('full_name', e.target.value)} />
        <TextInput label="Mobile Number" required
          value={formValues.mobile_number || ''}
          onChange={(e) => handleChange('mobile_number', e.target.value)} />
        <TextInput label="Email Address"
          value={formValues.email_address || ''}
          onChange={(e) => handleChange('email_address', e.target.value)} />


           <TextInput label="Alternate Full Name" 
          value={formValues.alt_full_name || ''}
          onChange={(e) => handleChange('alt_full_name', e.target.value)} />
        <TextInput label="Alternate Mobile Number" 
          value={formValues.alt_mobile_number || ''}
          onChange={(e) => handleChange('alt_mobile_number', e.target.value)} />
        <Select label="Configuration" data={configurationList}
          value={formValues.configuration || ''}
          onChange={(val) => handleChange('configuration', val)} />
        <Select label="Property Type" data={propertyTypeList}
          value={formValues.propertyType || ''}
          onChange={(val) => handleChange('propertyType', val)} />
        <TextInput label="Property Name"
          value={formValues.propertyName || ''}
          onChange={(e) => handleChange('propertyName', e.target.value)} />
        <TextInput label="Address"
          value={formValues.address || ''}
          onChange={(e) => handleChange('address', e.target.value)} />
        <Select label="City" data={cityList}
          value={formValues.city || ''}
          onChange={(val) => handleChange('city', val)} />
        <Select label="Requirement Type" data={requirementTypeList}
          value={formValues.requirementType || ''}
          onChange={(val) => handleChange('requirementType', val)} />
        <Select label="Service Type" data={serviceTypeList}
          value={formValues.serviceType || ''}
          onChange={(val) => handleChange('serviceType', val)} />
        <TextInput type="date" label="Lead Acquisition Date"
          value={toInputDate(formValues.leadAcquisitionDate || '')}
          onChange={(e) => handleChange('leadAcquisitionDate', toDisplayDate(e.target.value))} />
        <TextInput type="date" label="Meeting Date"
          value={toInputDate(formValues.meeting_date || '')}
          onChange={(e) => handleChange('meeting_date', toDisplayDate(e.target.value))} />


           {userRole !== 'salesperson' ? (
        <Select label="Sales Rep"
          data={salesRepList.map((r) => ({ label: r.label, value: r.value }))}
          value={formValues.sales_rep || ''}
          onChange={(val) => {
            const rep = salesRepList.find((r) => r.value === val);
            handleChange('sales_rep', val);
            handleChange('sales_rep_id', rep?.uid || '');
            handleChange('sales_rep_branch', rep?.branch || '');
          }} />  ) : null}

                {userRole !== 'preSales' ? (
        <Select
          label="PreSales Rep"
          data={preSalesRepList.map((r) => ({ label: r.label, value: r.value }))}
          value={formValues.presales_name || ''}
          onChange={(val) => {
            const rep = preSalesRepList.find((r) => r.value === val);
            handleChange('presales_name', rep?.label || val);
            handleChange('presales_id', rep?.uid || '');
          }}
        />
      ) : null}
        <Select label="Source" data={sourceList}
          value={formValues.source || ''}
          onChange={(val) => handleChange('source', val)} />
        <Select label="Channel" data={channelList}
          value={formValues.channel || ''}
          onChange={(val) => handleChange('channel', val)} />
        <Select label="Medium" data={mediumList}
          value={formValues.medium || ''}
          onChange={(val) => handleChange('medium', val)} />
        <TextInput label="Campaign"
          value={formValues.campaign || ''}
          onChange={(e) => handleChange('campaign', e.target.value)} />
        {/* <Select label="Possession" data={possessionTypeList}
          value={formValues.possession || ''}
          onChange={(val) => handleChange('possession', val)} /> */}

         <TextInput type="date" label="Possession Date"
          value={toInputDate(formValues.possession || '')}
          onChange={(e) => handleChange('possession', toDisplayDate(e.target.value))} />

          <TextInput type="date" label="Move In Date"
          value={toInputDate(formValues.move_in_date || '')}
          onChange={(e) => handleChange('move_in_date', toDisplayDate(e.target.value))} />
        <TextInput label="Budget"
          value={formValues.budget || ''}
          onChange={(e) => handleChange('budget', e.target.value)} />
        <FileUploadField
          label="Floor Plan (.pdf, .doc, .docx, .jpg, .png)"
          name="floorPlan"
          accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
          currentUrls={isEdit ? formValues.floorPlan || [] : []}
        />
      </SimpleGrid>
    </Box>
  );

  // ─── Render ────────────────────────────────────────────────────
  return (
    <MantineProvider>

      {/* Notification toast */}
      {notification && (
        <Box style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
          <Notification
            icon={notification.type === 'success' ? <IconCheck size={18} /> : <IconX size={18} />}
            color={notification.type === 'success' ? 'teal' : 'red'}
            title={notification.type === 'success' ? 'Success' : 'Error'}
            onClose={() => setNotification(null)}
          >
            {notification.message}
          </Notification>
        </Box>
      )}

      {/* CREATE MODAL */}
      <Modal
        opened={createModalOpen}
        onClose={() => { setCreateModalOpen(false); setFormValues({}); resetFiles(); setProjectId(''); }}
        title={
          <Group>
            <Text fw={600}>Add Lead</Text>
            {projectId && <Text size="sm" color="dimmed">Project ID: {projectId}</Text>}
          </Group>
        }
        size="70%" centered
      >
        <Box px="md" py="md">{renderFormFields(false)}</Box>
        <Group justify="flex-end" mt="xl" px="md">
          <Button variant="subtle"
            onClick={() => { setCreateModalOpen(false); setFormValues({}); resetFiles(); setProjectId(''); }}>
            Cancel
          </Button>
          <Button onClick={handleCreateProduct} loading={isCreating || uploadLoading} disabled={!projectId}>
            {uploadLoading ? 'Uploading...' : isCreating ? 'Saving...' : 'Save Lead'}
          </Button>
        </Group>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        opened={editModalOpen}
        onClose={() => { setEditModalOpen(false); setFormValues({}); resetFiles(); }}
        title="Edit Lead" size="70%" centered
      >
        <Box px="md" py="md">{renderFormFields(true)}</Box>
        <Group justify="flex-end" mt="xl" px="md">
          <Button variant="subtle"
            onClick={() => { setEditModalOpen(false); setFormValues({}); resetFiles(); }}>
            Cancel
          </Button>
          <Button onClick={handleUpdateProduct} loading={isUpdating || uploadLoading}>
            {uploadLoading ? 'Uploading...' : isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Group>
      </Modal>

      {/* TABLE */}
      <MantineReactTable
        columns={columns}
        data={productsApiData}
        rowCount={totalCount}
        manualPagination
        manualFiltering
        manualSorting
          initialState={{
    columnVisibility: {
      email_address: false,
      address: false,
      city: false,
      requirementType: false,
      serviceType: false,
      source: false,
      channel: false,
      medium: false,
      campaign: false,
    },
  }}
  enableColumnHiding   // 👈 important (default true but keep it)
  enableColumnActions
        state={{ isLoading, showProgressBars: isFetching, pagination, columnFilters, sorting }}
        onPaginationChange={setPagination}
        onColumnFiltersChange={handleColumnFiltersChange}
        onSortingChange={setSorting}
        enableRowActions
        enableColumnOrdering
        enableClickToCopy={true}
        positionActionsColumn="first"
        enableGlobalFilter={false}
        mantineTableBodyRowProps={({ row }) => ({
          onClick: () => navigate(`/leads/${row.original.id}`),
          style: { cursor: 'pointer' },
        })}
        renderRowActions={({ row }) => (
          <Group spacing={0} onClick={(e) => e.stopPropagation()}>
            <ActionIcon color="blue" variant="light" onClick={() => handleEditClick(row.original)}>
              <IconEdit size={16} />
            </ActionIcon>
            {userRole === 'admin' && (
              <ActionIcon color="red" variant="light" loading={isDeleting}
                onClick={() => handleDeleteProduct(row.original.id)}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        )}
        renderTopToolbarCustomActions={() => (
          <Group spacing="xs">
            <Button onClick={() => { setFormValues({}); resetFiles(); setCreateModalOpen(true); }}>
              + Add Lead
            </Button>
            {userRole === 'admin' && (
              <Button variant="outline" leftIcon={<IconFileSpreadsheet size={16} />}
                onClick={() => setBulkImportOpen(true)}>
                Bulk Import
              </Button>
            )}
          </Group>
        )}
      />

      <BulkImportModal
        opened={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImportSuccess={() => {
          // fetchProducts() not needed — socket 'bulk:import:complete' triggers invalidation
          setBulkImportOpen(false);
          showNotification('success', 'Leads imported successfully');
        }}
      />

    </MantineProvider>
  );
}